import { Outlet, useNavigate } from "@tanstack/react-router"
import { useEffect } from "react"
import { SidebarProvider } from "@holigral/shared/ui/components/ui/sidebar"
import { useLocale } from "@holigral/shared/routing"
import AppSidebar from "../sidebar/AppSidebar"
import VerificationRequired from "../auth/VerificationRequired"
import { LoadingScreen } from "@/ui"
import { VerificationStatus, useAuth, useRedirectAfterLogin } from "@/auth"
import { useMe, useSyncUserLocale } from "@/profile"
import { Notifications } from "@/notification"

// Long enough to be negligible, short enough that nobody sits in a shell the
// backend has already started refusing.
const VERIFICATION_POLL_MS = 5 * 60 * 1000

export default function UserRoute() {
  const { isLoading, isAuthenticated } = useAuth()
  // useMe skips until AuthWrapper has an access token — see its comment for
  // why an unauthenticated ask would poison `me` for the whole session.
  const { data: me, isLoading: isMeLoading } = useMe()

  // A second subscription to the same cache entry, existing only to poll.
  // Verification status is a function of time, so a session left open crosses
  // the grace boundary with no request to reveal it — the shell would stay up
  // while the backend had already started refusing. Only accounts actually
  // inside the grace window poll; everyone else keeps the plain subscription.
  useMe({
    skip: me?.verificationStatus !== VerificationStatus.Unverified,
    pollingInterval: VERIFICATION_POLL_MS,
  })

  const { setRedirectUrl } = useRedirectAfterLogin()
  const navigate = useNavigate()
  // App routes are not locale-prefixed; bounce to login in the user's locale.
  const locale = useLocale()
  // The signed-in user's stored locale is authoritative across the app.
  useSyncUserLocale()

  useEffect(() => {
    if (isLoading) {
      return
    }
    if (!isAuthenticated) {
      setRedirectUrl()
      void navigate({ to: "/$locale/auth", params: { locale }, replace: true })
    }
  }, [isLoading, isAuthenticated, navigate, setRedirectUrl, locale])

  // Waiting on `me` too, so the app never flashes before the gate resolves.
  if (isLoading || isMeLoading || !isAuthenticated) {
    return <LoadingScreen />
  }

  // Replaces the shell rather than rendering inside it, so an expired user
  // has no route left to reach.
  if (me?.verificationStatus === VerificationStatus.UnverifiedExpired) {
    return <VerificationRequired />
  }

  return (
    <SidebarProvider>
      <AppSidebar />
      <div className="flex h-dvh min-w-0 flex-1 flex-col">
        <Notifications />
        <Outlet />
      </div>
    </SidebarProvider>
  )
}
